import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'
import type { Retiro, Configuracion } from '@/types/database'

const s = StyleSheet.create({
  page:       { padding: 40, fontFamily: 'Helvetica', fontSize: 9, color: '#1a1a0e' },
  biz:        { fontSize: 14, fontWeight: 'bold', marginBottom: 3 },
  meta:       { fontSize: 9, color: '#6b6b5a', marginBottom: 2 },
  divider:    { borderBottomWidth: 1, borderBottomColor: '#e8e4dc', marginVertical: 12 },
  row:        { flexDirection: 'row', gap: 24, marginBottom: 12 },
  block:      { flex: 1 },
  eyebrow:    { fontSize: 7, color: '#6b6b5a', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 },
  val:        { fontSize: 10, fontWeight: 'bold' },
  theadRow:   { flexDirection: 'row', backgroundColor: '#f0ede8', paddingVertical: 5, paddingHorizontal: 8 },
  tbodyRow:   { flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 8, borderBottomWidth: 1, borderBottomColor: '#f0ede8' },
  colProduct: { flex: 3 },
  colNum:     { width: 56, textAlign: 'right' },
  thText:     { fontSize: 7.5, color: '#6b6b5a', textTransform: 'uppercase', letterSpacing: 0.5 },
  tdText:     { fontSize: 9 },
  tdMarca:    { fontSize: 7, color: '#6b6b5a' },
  ok:         { color: '#22c55e' },
  warn:       { color: '#f59e0b', fontWeight: 'bold' },
  footer:     { marginTop: 24, fontSize: 7.5, color: '#9b9b8a', textAlign: 'center' },
})

export function RetiroDocument({ retiro, config }: { retiro: Retiro; config: Configuracion }) {
  const venta = (retiro as unknown as {
    ventas?: { numero: number; tipo: string; clientes?: { nombre: string } }
  }).ventas

  const items = retiro.retiro_items ?? []

  const fecha = new Date(retiro.created_at).toLocaleDateString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })

  const estadoLabel: Record<string, string> = {
    pendiente: 'Pendiente', parcial: 'Parcial', completo: 'Completo',
  }

  return (
    <Document>
      <Page size="A4" style={s.page}>

        {/* Encabezado negocio */}
        <View>
          <Text style={s.biz}>{config.nombre_negocio}</Text>
          {config.direccion ? <Text style={s.meta}>{config.direccion}</Text> : null}
          {config.telefono ? <Text style={s.meta}>{config.telefono}</Text> : null}
        </View>

        <View style={s.divider} />

        {/* Info retiro */}
        <View style={s.row}>
          <View style={s.block}>
            <Text style={s.eyebrow}>Retiro</Text>
            <Text style={s.val}>#{String(retiro.numero).padStart(5, '0')}</Text>
          </View>
          <View style={s.block}>
            <Text style={s.eyebrow}>Fecha</Text>
            <Text style={s.val}>{fecha}</Text>
          </View>
          <View style={s.block}>
            <Text style={s.eyebrow}>Estado</Text>
            <Text style={s.val}>{estadoLabel[retiro.estado] ?? retiro.estado}</Text>
          </View>
        </View>

        {venta ? (
          <View style={{ marginBottom: 12 }}>
            <Text style={s.eyebrow}>Venta de referencia</Text>
            <Text style={s.val}>
              #{String(venta.numero).padStart(5, '0')}
              {venta.clientes?.nombre ? `  ·  ${venta.clientes.nombre}` : ''}
              {venta.tipo === 'mayorista' ? '  ·  Mayorista' : ''}
            </Text>
          </View>
        ) : null}

        <View style={s.divider} />

        {/* Tabla items */}
        <View style={s.theadRow}>
          <Text style={[s.colProduct, s.thText]}>Producto</Text>
          <Text style={[s.colNum, s.thText]}>Solicitado</Text>
          <Text style={[s.colNum, s.thText]}>Entregado</Text>
          <Text style={[s.colNum, s.thText]}>Pendiente</Text>
        </View>

        {items.map(item => {
          const vi = (item as unknown as {
            venta_items?: { productos?: { nombre: string; marca?: string } }
          }).venta_items
          const nombre   = vi?.productos?.nombre ?? '—'
          const marca    = vi?.productos?.marca
          const pendiente = item.cantidad_solicitada - item.cantidad_entregada

          return (
            <View key={item.id} style={s.tbodyRow}>
              <View style={s.colProduct}>
                <Text style={s.tdText}>{nombre}</Text>
                {marca ? <Text style={s.tdMarca}>{marca}</Text> : null}
              </View>
              <Text style={[s.colNum, s.tdText]}>{item.cantidad_solicitada}</Text>
              <Text style={[s.colNum, s.tdText, item.cantidad_entregada > 0 ? s.ok : {}]}>
                {item.cantidad_entregada}
              </Text>
              <Text style={[s.colNum, s.tdText, pendiente > 0 ? s.warn : s.ok]}>
                {pendiente > 0 ? String(pendiente) : '✓'}
              </Text>
            </View>
          )
        })}

        {retiro.notas ? (
          <View style={{ marginTop: 16 }}>
            <Text style={s.eyebrow}>Notas</Text>
            <Text style={{ fontSize: 9, color: '#3a3a2e', marginTop: 3 }}>{retiro.notas}</Text>
          </View>
        ) : null}

        <Text style={s.footer}>MayoExpress · Documento generado automáticamente</Text>
      </Page>
    </Document>
  )
}
